import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { UserProvider } from './context/UserContext';
import ProtectedRoute from './components/ProtectedRoute';
import UserProtectedRoute from './components/UserProtectedRoute';
import ScrollToTop from './components/ScrollToTop';

// Public Pages
import Home from './pages/public/Home';
import StationRooms from './pages/public/StationRooms';
import BookingRequest from './pages/public/BookingRequest';
import BookingStatus from './pages/public/BookingStatus';
import TermsAndConditions from './pages/public/TermsAndConditions';
import UserLogin from './pages/public/UserLogin';
import Blog from './pages/public/Blog';
import BlogBhusaval from './pages/public/BlogBhusaval';
import BlogKannur from './pages/public/BlogKannur';
import BlogKottayam from './pages/public/BlogKottayam';
import MyBookings from './pages/public/MyBookings';
import HelpCenter from './pages/public/HelpCenter';
import CreateTicket from './pages/public/CreateTicket';
import TrackTicket from './pages/public/TrackTicket';
import TicketDetail from './pages/public/TicketDetail';

// Auth Pages
import Login from './pages/auth/Login';

// Super Admin Pages
import SuperAdminDashboard from './pages/superadmin/Dashboard';
import StationsManagement from './pages/superadmin/Stations';
import CategoriesManagement from './pages/superadmin/Categories';
import RoomsManagement from './pages/superadmin/Rooms';
import AdminsManagement from './pages/superadmin/Admins';
import SettingsManagement from './pages/superadmin/Settings';
import SupportDashboard from './pages/superadmin/SupportDashboard';

// Station Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import BookingRequests from './pages/admin/BookingRequests';
import AdvanceBookingsSchedule from './pages/admin/AdvanceBookingsSchedule';
import RoomStatus from './pages/admin/RoomStatus';
import Billing from './pages/admin/Billing';
import SupportTickets from './pages/admin/SupportTickets';
import SupportTicketDetail from './pages/admin/SupportTicketDetail';

function App() {
  return (
    <AuthProvider>
      <UserProvider>
        <Router>
          <ScrollToTop />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#1a1a3e',
                color: '#fff',
                borderRadius: '12px',
                padding: '16px',
              },
              success: {
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#fff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/station/:id" element={<StationRooms />} />
            <Route path="/booking/status/:id" element={<BookingStatus />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/travel-guide-bhusaval" element={<BlogBhusaval />} />
            <Route path="/blog/travel-guide-kannur" element={<BlogKannur />} />
            <Route path="/blog/travel-guide-kottayam" element={<BlogKottayam />} />
            <Route path="/my-bookings" element={<MyBookings />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/support/new" element={<CreateTicket />} />
            <Route path="/support/track" element={<TrackTicket />} />
            <Route path="/support/ticket/:ticketNumber" element={<TicketDetail />} />

            {/* User Auth Route */}
            <Route path="/user/login" element={<UserLogin />} />

            {/* Protected Booking Route - Requires User Login */}
            <Route path="/book/:stationId" element={
              <UserProtectedRoute>
                <BookingRequest />
              </UserProtectedRoute>
            } />

            {/* Admin Auth Routes */}
            <Route path="/login" element={<Login />} />

            {/* Super Admin Routes */}
            <Route path="/superadmin" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/stations" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <StationsManagement />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/categories" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <CategoriesManagement />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/rooms" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <RoomsManagement />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/admins" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AdminsManagement />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/settings" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SettingsManagement />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/support" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SupportDashboard />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/support/tickets" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SupportTickets />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/support/:id" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SupportTicketDetail />
              </ProtectedRoute>
            } />

            {/* Station Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={['station_admin', 'super_admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/requests" element={
              <ProtectedRoute allowedRoles={['station_admin', 'super_admin']}>
                <BookingRequests />
              </ProtectedRoute>
            } />
            <Route path="/admin/schedule" element={
              <ProtectedRoute allowedRoles={['station_admin', 'super_admin']}>
                <AdvanceBookingsSchedule />
              </ProtectedRoute>
            } />
            <Route path="/admin/rooms" element={
              <ProtectedRoute allowedRoles={['station_admin', 'super_admin']}>
                <RoomStatus />
              </ProtectedRoute>
            } />
            <Route path="/admin/billing" element={
              <ProtectedRoute allowedRoles={['station_admin', 'super_admin']}>
                <Billing />
              </ProtectedRoute>
            } />
            <Route path="/admin/support" element={
              <ProtectedRoute allowedRoles={['station_admin', 'super_admin']}>
                <SupportTickets />
              </ProtectedRoute>
            } />
            <Route path="/admin/support/:id" element={
              <ProtectedRoute allowedRoles={['station_admin', 'super_admin']}>
                <SupportTicketDetail />
              </ProtectedRoute>
            } />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </UserProvider>
    </AuthProvider>
  );
}

export default App;
